import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy } from "lucide-react";

export interface ColorPickerProps {
    value: string;
    onChange: (hexColor: string) => void;
    presetColors?: { id: string; hex: string; label?: string }[];
}

interface HsvColor {
    h: number; // 0-360
    s: number; // 0-100
    v: number; // 0-100
}

interface RgbColor {
    r: number; // 0-255
    g: number; // 0-255
    b: number; // 0-255
}

interface HslColor {
    h: number; // 0-360
    s: number; // 0-100
    l: number; // 0-100
}

// Convert Hex to RGB
function hexToRgb(hex: string): RgbColor {
    const cleanHex = hex.replace(/^#/, "");
    const fullHex =
        cleanHex.length === 3
            ? cleanHex
                  .split("")
                  .map((c) => c + c)
                  .join("")
            : cleanHex;

    const num = parseInt(fullHex, 16);
    if (isNaN(num) || fullHex.length !== 6) {
        return { r: 255, g: 0, b: 134 }; // default #ff0086
    }

    return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255,
    };
}

// Convert RGB to Hex
function rgbToHex(rgb: RgbColor): string {
    const r = Math.max(0, Math.min(255, Math.round(rgb.r)))
        .toString(16)
        .padStart(2, "0");
    const g = Math.max(0, Math.min(255, Math.round(rgb.g)))
        .toString(16)
        .padStart(2, "0");
    const b = Math.max(0, Math.min(255, Math.round(rgb.b)))
        .toString(16)
        .padStart(2, "0");
    return `#${r}${g}${b}`.toUpperCase();
}

// Convert RGB to HSV
function rgbToHsv(rgb: RgbColor): HsvColor {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
        if (max === r) {
            h = ((g - b) / delta) % 6;
        } else if (max === g) {
            h = (b - r) / delta + 2;
        } else {
            h = (r - g) / delta + 4;
        }
        h = Math.round(h * 60);
        if (h < 0) {
            h += 360;
        }
    }

    const s = max === 0 ? 0 : Math.round((delta / max) * 100);
    const v = Math.round(max * 100);

    return { h, s, v };
}

// Convert HSV to RGB
function hsvToRgb(hsv: HsvColor): RgbColor {
    const h = hsv.h / 60;
    const s = hsv.s / 100;
    const v = hsv.v / 100;

    const c = v * s;
    const x = c * (1 - Math.abs((h % 2) - 1));
    const m = v - c;

    let r = 0;
    let g = 0;
    let b = 0;

    if (h >= 0 && h < 1) {
        r = c;
        g = x;
    } else if (h >= 1 && h < 2) {
        r = x;
        g = c;
    } else if (h >= 2 && h < 3) {
        g = c;
        b = x;
    } else if (h >= 3 && h < 4) {
        g = x;
        b = c;
    } else if (h >= 4 && h < 5) {
        r = x;
        b = c;
    } else if (h >= 5 && h <= 6) {
        r = c;
        b = x;
    }

    return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255),
    };
}

// Convert RGB to HSL
function rgbToHsl(rgb: RgbColor): HslColor {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) {
        return { h: 0, s: 0, l: Math.round(l * 100) };
    }

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    let h = 0;
    if (max === r) {
        h = ((g - b) / d) % 6;
    } else if (max === g) {
        h = (b - r) / d + 2;
    } else {
        h = (r - g) / d + 4;
    }

    h = Math.round(h * 60);
    if (h < 0) {
        h += 360;
    }

    return {
        h,
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
}

// Convert HSL to RGB
function hslToRgb(hsl: HslColor): RgbColor {
    const h = hsl.h / 360;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    if (s === 0) {
        const val = Math.round(l * 255);
        return { r: val, g: val, b: val };
    }

    const hue2rgb = (p: number, q: number, t: number): number => {
        let tt = t;
        if (tt < 0) {
            tt += 1;
        }
        if (tt > 1) {
            tt -= 1;
        }
        if (tt < 1 / 6) {
            return p + (q - p) * 6 * tt;
        }
        if (tt < 1 / 2) {
            return q;
        }
        if (tt < 2 / 3) {
            return p + (q - p) * (2 / 3 - tt) * 6;
        }
        return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    return {
        r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        g: Math.round(hue2rgb(p, q, h) * 255),
        b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
}

const DEFAULT_PRESETS = [
    { id: "primary", hex: "#FF0086", label: "Primary Pink" },
    { id: "emerald", hex: "#10B981", label: "Emerald" },
    { id: "blue", hex: "#3B82F6", label: "Blue" },
    { id: "purple", hex: "#A855F7", label: "Purple" },
    { id: "amber", hex: "#F59E0B", label: "Amber" },
    { id: "rose", hex: "#F43F5E", label: "Rose" },
    { id: "cyan", hex: "#06B6D4", label: "Cyan" },
    { id: "indigo", hex: "#6366F1", label: "Indigo" },
    { id: "orange", hex: "#F97316", label: "Orange" },
    { id: "teal", hex: "#14B8A6", label: "Teal" },
    { id: "dark", hex: "#18181B", label: "Dark" },
    { id: "slate", hex: "#64748B", label: "Slate" },
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
    value,
    onChange,
    presetColors = DEFAULT_PRESETS,
}) => {
    const { t } = useTranslation("global");
    const [format, setFormat] = useState<"HEX" | "RGB" | "HSL">("HEX");

    // Internal HSV state
    const [hsv, setHsv] = useState<HsvColor>(() => {
        const rgb = hexToRgb(value || "#FF0086");
        return rgbToHsv(rgb);
    });

    const [hexInput, setHexInput] = useState<string>(value || "#FF0086");
    const [rgbInputs, setRgbInputs] = useState<RgbColor>(() =>
        hexToRgb(value || "#FF0086")
    );
    const [hslInputs, setHslInputs] = useState<HslColor>(() =>
        rgbToHsl(hexToRgb(value || "#FF0086"))
    );
    const [copied, setCopied] = useState<boolean>(false);

    const satAreaRef = useRef<HTMLDivElement>(null);
    const hueBarRef = useRef<HTMLDivElement>(null);
    const isSatDragging = useRef<boolean>(false);
    const isHueDragging = useRef<boolean>(false);

    // Sync from external value when it changes
    useEffect(() => {
        const rgb = hexToRgb(value || "#FF0086");
        const newHsv = rgbToHsv(rgb);
        setHsv(newHsv);
        setHexInput(rgbToHex(rgb));
        setRgbInputs(rgb);
        setHslInputs(rgbToHsl(rgb));
    }, [value]);

    // Update all inputs from HSV
    const syncFromHsv = useCallback((newHsv: HsvColor): void => {
        setHsv(newHsv);
        const rgb = hsvToRgb(newHsv);
        const hex = rgbToHex(rgb);
        const hsl = rgbToHsl(rgb);
        setHexInput(hex);
        setRgbInputs(rgb);
        setHslInputs(hsl);
    }, []);

    // Saturation / Value Area Pointer Handlers
    const updateSatValFromPointer = (
        clientX: number,
        clientY: number
    ): void => {
        const area = satAreaRef.current;
        if (!area) {
            return;
        }

        const rect = area.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
        const y = Math.max(0, Math.min(rect.height, clientY - rect.top));

        const s = Math.round((x / rect.width) * 100);
        const v = Math.round((1 - y / rect.height) * 100);

        syncFromHsv({ ...hsv, s, v });
    };

    const handleSatPointerDown = (
        e: React.PointerEvent<HTMLDivElement>
    ): void => {
        e.preventDefault();
        e.stopPropagation();
        try {
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
        } catch {}
        isSatDragging.current = true;
        updateSatValFromPointer(e.clientX, e.clientY);
    };

    const handleSatPointerMove = (
        e: React.PointerEvent<HTMLDivElement>
    ): void => {
        if (!isSatDragging.current) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        updateSatValFromPointer(e.clientX, e.clientY);
    };

    const handleSatPointerUp = (
        e: React.PointerEvent<HTMLDivElement>
    ): void => {
        if (isSatDragging.current) {
            try {
                (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            } catch {}
            isSatDragging.current = false;
        }
    };

    // Hue Slider Pointer Handlers
    const updateHueFromPointer = (clientX: number): void => {
        const bar = hueBarRef.current;
        if (!bar) {
            return;
        }

        const rect = bar.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
        const h = Math.round((x / rect.width) * 360) % 360;

        syncFromHsv({ ...hsv, h });
    };

    const handleHuePointerDown = (
        e: React.PointerEvent<HTMLDivElement>
    ): void => {
        e.preventDefault();
        e.stopPropagation();
        try {
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
        } catch {}
        isHueDragging.current = true;
        updateHueFromPointer(e.clientX);
    };

    const handleHuePointerMove = (
        e: React.PointerEvent<HTMLDivElement>
    ): void => {
        if (!isHueDragging.current) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        updateHueFromPointer(e.clientX);
    };

    const handleHuePointerUp = (
        e: React.PointerEvent<HTMLDivElement>
    ): void => {
        if (isHueDragging.current) {
            try {
                (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            } catch {}
            isHueDragging.current = false;
        }
    };

    // Manual input updates
    const handleHexInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const val = e.target.value;
        setHexInput(val);
        if (/^#?[0-9A-Fa-f]{6}$/.test(val)) {
            const rgb = hexToRgb(val);
            syncFromHsv(rgbToHsv(rgb));
        }
    };

    const handleRgbInputChange = (
        key: "r" | "g" | "b",
        numVal: number
    ): void => {
        const safe = isNaN(numVal) ? 0 : Math.max(0, Math.min(255, numVal));
        const updated = { ...rgbInputs, [key]: safe };
        setRgbInputs(updated);
        syncFromHsv(rgbToHsv(updated));
    };

    const handleHslInputChange = (
        key: "h" | "s" | "l",
        numVal: number
    ): void => {
        const max = key === "h" ? 360 : 100;
        const safe = isNaN(numVal) ? 0 : Math.max(0, Math.min(max, numVal));
        const updated = { ...hslInputs, [key]: safe };
        setHslInputs(updated);
        const rgb = hslToRgb(updated);
        syncFromHsv(rgbToHsv(rgb));
    };

    const handleSelectPreset = (presetHex: string): void => {
        const rgb = hexToRgb(presetHex);
        syncFromHsv(rgbToHsv(rgb));
    };

    // Propagate change
    useEffect(() => {
        const currentHex = hexInput.startsWith("#") ? hexInput : `#${hexInput}`;
        if (currentHex !== value) {
            onChange(currentHex);
        }
    }, [hexInput]);

    const handleCopy = (): void => {
        let copyText = hexInput;
        if (format === "RGB") {
            copyText = `rgb(${rgbInputs.r}, ${rgbInputs.g}, ${rgbInputs.b})`;
        } else {
            if (format === "HSL") {
                copyText = `hsl(${hslInputs.h}, ${hslInputs.s}%, ${hslInputs.l}%)`;
            }
        }

        navigator.clipboard.writeText(copyText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const currentColorHex = rgbToHex(hsvToRgb(hsv));
    const pureHueHex = rgbToHex(hsvToRgb({ h: hsv.h, s: 100, v: 100 }));

    return (
        <div className="space-y-4">
            {/* 2D Saturation / Value Gradient Board */}
            <div
                ref={satAreaRef}
                onPointerDown={handleSatPointerDown}
                onPointerMove={handleSatPointerMove}
                onPointerUp={handleSatPointerUp}
                onPointerCancel={handleSatPointerUp}
                style={{ backgroundColor: pureHueHex }}
                className="relative w-full h-44 rounded-2xl cursor-crosshair touch-none select-none overflow-hidden shadow-inner border border-slate-200 dark:border-zinc-700"
            >
                {/* White gradient (horizontal) */}
                <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent pointer-events-none" />
                {/* Black gradient (vertical) */}
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent pointer-events-none" />

                {/* Thumb indicator */}
                <div
                    className="absolute w-5 h-5 rounded-full border-2 border-white shadow-[0_0_4px_rgba(0,0,0,0.6)] -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-transform"
                    style={{
                        left: `${hsv.s}%`,
                        top: `${100 - hsv.v}%`,
                        backgroundColor: currentColorHex,
                    }}
                />
            </div>

            {/* Hue Rainbow Slider */}
            <div className="space-y-1.5">
                <div
                    ref={hueBarRef}
                    onPointerDown={handleHuePointerDown}
                    onPointerMove={handleHuePointerMove}
                    onPointerUp={handleHuePointerUp}
                    onPointerCancel={handleHuePointerUp}
                    className="relative w-full h-5 rounded-xl cursor-pointer touch-none select-none shadow-xs border-0"
                    style={{
                        background:
                            "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
                    }}
                >
                    {/* Thumb indicator: White background */}
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-4.5 h-7 rounded-full bg-white shadow-md shadow-black/30 -translate-x-1/2 pointer-events-none"
                        style={{ left: `${(hsv.h / 360) * 100}%` }}
                    />
                </div>
            </div>

            {/* Preset Swatches Palette with Centered Title */}
            <div>
                <span className="block text-[11px] font-bold uppercase text-slate-500 dark:text-zinc-400 mb-2.5 text-center">
                    Colores rápidos
                </span>
                <div className="flex items-center justify-start gap-3 flex-wrap py-1">
                    {presetColors.map((preset) => {
                        const isSelected =
                            currentColorHex.toLowerCase() ===
                            preset.hex.toLowerCase();

                        return (
                            <button
                                key={preset.id}
                                type="button"
                                onClick={() => handleSelectPreset(preset.hex)}
                                style={{ backgroundColor: preset.hex }}
                                className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-all cursor-pointer shadow-xs flex items-center justify-center ${
                                    isSelected
                                        ? "ring-2 ring-offset-1 ring-primary dark:ring-offset-zinc-900 scale-105"
                                        : "opacity-85 hover:opacity-100 hover:scale-105"
                                }`}
                                title={preset.label || preset.hex}
                                aria-label={preset.label || preset.hex}
                            >
                                {isSelected && (
                                    <Check className="w-4 h-4 text-white drop-shadow-xs" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Format Switcher & Value Adjuster */}
            <div className="space-y-3 pt-1">
                {/* Row 1: Format Selector Tabs (Full Width) with smooth sliding pill */}
                <div className="relative grid grid-cols-3 p-1 bg-stone-100 dark:bg-zinc-800 rounded-2xl border border-slate-200/80 dark:border-zinc-700/80 w-full select-none">
                    {/* Sliding active pill indicator */}
                    <div
                        className="absolute top-1 bottom-1 rounded-xl bg-white dark:bg-zinc-900 shadow-xs transition-transform duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
                        style={{
                            width: "calc((100% - 8px) / 3)",
                            transform: `translateX(${
                                format === "HEX"
                                    ? "0%"
                                    : format === "RGB"
                                      ? "100%"
                                      : "200%"
                            })`,
                        }}
                    />

                    {(["HEX", "RGB", "HSL"] as const).map((fmt) => (
                        <button
                            key={fmt}
                            type="button"
                            onClick={() => setFormat(fmt)}
                            className={`relative z-10 py-1.5 rounded-xl text-xs font-black transition-colors duration-200 cursor-pointer text-center ${
                                format === fmt
                                    ? "text-slate-900 dark:text-white"
                                    : "text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
                            }`}
                        >
                            {fmt}
                        </button>
                    ))}
                </div>

                {/* Row 2: Color Preview Swatch with Current Code & Copy Action */}
                <div className="flex items-center justify-between gap-3 p-2.5 bg-white dark:bg-zinc-900 rounded-2xl border border-slate-200 dark:border-zinc-700">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-10 h-10 rounded-full border border-black/10 dark:border-white/10 shadow-xs flex-shrink-0"
                            style={{ backgroundColor: currentColorHex }}
                        />
                        <div className="flex flex-col">
                            <span className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wide font-mono">
                                {format === "HEX" && currentColorHex}
                                {format === "RGB" &&
                                    `RGB(${rgbInputs.r}, ${rgbInputs.g}, ${rgbInputs.b})`}
                                {format === "HSL" &&
                                    `HSL(${hslInputs.h}, ${hslInputs.s}%, ${hslInputs.l}%)`}
                            </span>
                            <span className="text-[10px] text-slate-500 dark:text-zinc-400 font-semibold font-mono">
                                {format !== "HEX" && currentColorHex}
                                {format === "HEX" &&
                                    `RGB(${rgbInputs.r}, ${rgbInputs.g}, ${rgbInputs.b})`}
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleCopy}
                        className={`h-11 w-11 rounded-2xl border transition-all flex items-center justify-center cursor-pointer flex-shrink-0 ${
                            copied
                                ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-500 scale-105 shadow-2xs"
                                : "bg-stone-50 dark:bg-zinc-800 border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 hover:text-primary hover:border-slate-300 dark:hover:border-zinc-600"
                        }`}
                        title={copied ? "Copiado" : "Copiar"}
                        aria-label={copied ? "Copiado" : "Copiar"}
                    >
                        {copied ? (
                            <Check className="w-5 h-5 text-emerald-500 animate-in zoom-in-50 duration-200" />
                        ) : (
                            <Copy className="w-5 h-5" />
                        )}
                    </button>
                </div>

                {/* Row 3: Inputs to change values with smooth entrance */}
                <div
                    key={format}
                    className="animate-in fade-in zoom-in-95 duration-200"
                >
                    {format === "HEX" && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase w-10 text-center">
                                HEX
                            </span>
                            <input
                                type="text"
                                value={hexInput}
                                onChange={handleHexInputChange}
                                placeholder="#FF0086"
                                maxLength={7}
                                className="flex-1 px-3.5 py-2 text-sm font-mono font-black uppercase rounded-2xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-slate-900 dark:text-white outline-hidden"
                            />
                        </div>
                    )}

                    {format === "RGB" && (
                        <div className="grid grid-cols-3 gap-2">
                            {(["r", "g", "b"] as const).map((channel) => (
                                <div
                                    key={channel}
                                    className="flex items-center gap-1.5"
                                >
                                    <span className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase">
                                        {channel}
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={255}
                                        value={rgbInputs[channel]}
                                        onChange={(e) =>
                                            handleRgbInputChange(
                                                channel,
                                                parseInt(e.target.value, 10)
                                            )
                                        }
                                        className="w-full px-2.5 py-2 text-xs font-mono font-black text-center rounded-2xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-slate-900 dark:text-white outline-hidden"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {format === "HSL" && (
                        <div className="grid grid-cols-3 gap-2">
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase">
                                    H
                                </span>
                                <input
                                    type="number"
                                    min={0}
                                    max={360}
                                    value={hslInputs.h}
                                    onChange={(e) =>
                                        handleHslInputChange(
                                            "h",
                                            parseInt(e.target.value, 10)
                                        )
                                    }
                                    className="w-full px-2.5 py-2 text-xs font-mono font-black text-center rounded-2xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-slate-900 dark:text-white outline-hidden"
                                />
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase">
                                    S%
                                </span>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={hslInputs.s}
                                    onChange={(e) =>
                                        handleHslInputChange(
                                            "s",
                                            parseInt(e.target.value, 10)
                                        )
                                    }
                                    className="w-full px-2.5 py-2 text-xs font-mono font-black text-center rounded-2xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-slate-900 dark:text-white outline-hidden"
                                />
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-slate-500 dark:text-zinc-400 uppercase">
                                    L%
                                </span>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={hslInputs.l}
                                    onChange={(e) =>
                                        handleHslInputChange(
                                            "l",
                                            parseInt(e.target.value, 10)
                                        )
                                    }
                                    className="w-full px-2.5 py-2 text-xs font-mono font-black text-center rounded-2xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-slate-900 dark:text-white outline-hidden"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ColorPicker;
